import React from "react";
import { Eye } from "lucide-react";

// Category → gradient placeholder (dipakai kalau preview_image kosong).
const CATEGORY_GRADIENT = {
  grocery: "from-emerald-500/30 to-lime-500/10",
  beauty: "from-pink-500/30 to-fuchsia-500/10",
  fashion: "from-slate-500/30 to-zinc-500/10",
  restaurant: "from-orange-500/30 to-red-500/10",
  electronics: "from-sky-500/30 to-cyan-500/10",
  pharmacy: "from-teal-500/30 to-emerald-500/10",
  other: "from-white/10 to-white/5",
};

const CATEGORY_ICON = {
  grocery: "🥚",
  beauty: "✨",
  fashion: "👗",
  restaurant: "🍽",
  electronics: "🖥",
  pharmacy: "💊",
  other: "🎨",
};

/**
 * TemplatePreviewCard — reusable card untuk daftar/pilih template.
 * Props:
 *   template            : { template_key, template_name, template_category, description,
 *                           preview_image, thumbnail, version, is_active }
 *   selected            : boolean — highlight border kalau true
 *   onClick             : klik card (biasanya untuk pilih di dialog change)
 *   compact             : boolean — mode kecil untuk dialog pilih template
 */
export default function TemplatePreviewCard({ template, selected, onClick, compact }) {
  if (!template) return null;
  const cat = (template.template_category || "other").toLowerCase();
  const grad = CATEGORY_GRADIENT[cat] || CATEGORY_GRADIENT.other;
  const icon = CATEGORY_ICON[cat] || CATEGORY_ICON.other;
  const inactive = !template.is_active;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-2xl border transition-all overflow-hidden bg-white/5 hover:bg-white/[0.07] ${
        selected ? "border-white ring-2 ring-white/40" : "border-white/10 hover:border-white/30"
      } ${inactive ? "opacity-60" : ""} ${compact ? "" : "min-w-[240px]"}`}
      data-testid={`template-card-${template.template_key}`}
    >
      {/* Preview */}
      <div className={`aspect-[16/9] relative overflow-hidden bg-gradient-to-br ${grad}`}>
        {template.preview_image ? (
          <img src={template.preview_image} alt={template.template_name} className="w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 grid place-items-center">
            <div className="text-5xl opacity-70">{icon}</div>
          </div>
        )}
        <div className="absolute top-2 right-2 flex items-center gap-1">
          {inactive && (
            <span className="text-[9px] font-black uppercase tracking-wider bg-rose-500/80 text-white px-1.5 py-0.5 rounded">
              INACTIVE
            </span>
          )}
          <span className="text-[9px] font-mono bg-black/40 text-white/80 px-1.5 py-0.5 rounded" data-testid={`template-version-${template.template_key}`}>
            v{template.version}
          </span>
        </div>
      </div>

      {/* Info */}
      <div className={compact ? "p-3" : "p-4"}>
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-[10px] font-black uppercase tracking-wider text-white/50`} data-testid={`template-category-${template.template_key}`}>
            {cat}
          </span>
        </div>
        <div className={`text-white font-display font-black ${compact ? "text-sm" : "text-base"} truncate`}>
          {template.template_name}
        </div>
        {!compact && template.description && (
          <div className="text-[11px] text-white/50 mt-1 line-clamp-2">{template.description}</div>
        )}
        {!compact && (
          <div className="flex items-center gap-1 text-[10px] text-white/40 mt-2">
            <Eye size={10} /> <span className="font-mono">{template.template_key}</span>
          </div>
        )}
      </div>
    </button>
  );
}
